import './archive.css';

import { useRecoilState, useRecoilValue } from 'recoil';
import { userConfigState } from '../../recoil/atoms/userConfigState';
import { allUserConfigsState } from '../../recoil/atoms/allUserConfigsState';
import { initialReportFiltersState, reportFiltersState } from '../../recoil/atoms/reportFiltersState';
import { useWindowSize } from '../../../common/hooks/WindowSize';

import { Rating } from '@mui/material';
import ReplayIcon from '@mui/icons-material/Replay';

import { translate } from '../../../common/language/translations';
import { UserDropDown } from '../../components/controls/dropDowns/UserDropDown';
import { DropDownWithIcons } from '../../components/controls/dropDowns/DropDownWithIcons';
import { ReportType } from '../../types/report';
import { InputField } from '../../components/controls/input/InputField';
import { getTenantColor } from '../../static/tenantConfigs';
import { IconButton } from '../../components/controls/buttons/IconButton';
import { useMemo } from 'react';
import { UserConfig } from '../../types/user';
import { getSortDistanceAccess } from '../../utils/accessUtils';
import { isFokusUser } from '../../utils/userConfigUtils';


// export type ReportAccess =
//   | 'full'
//   | 'academy' // all reports submitted with this access will be "academyReports"
//   | 'personal'
//   | 'none';


interface ReportFiltersProps {
  currentReportFilterExpanded: string | undefined;
  setCurrentReportFilterExpanded: (value: string | undefined) => void;
}


export const ReportFilters: React.FC<ReportFiltersProps> = ({
  currentReportFilterExpanded,
  setCurrentReportFilterExpanded
}) => {

  const userConfig = useRecoilValue(userConfigState);
  const allUsersWithSameTenant = useRecoilValue(allUserConfigsState);

  const { width } = useWindowSize();

  const [reportFilters, setReportFilters] = useRecoilState(reportFiltersState);


  const userDropDownOptions = useMemo(() => {
    let options: UserConfig[] = [];
    if (userConfig?.accessConfig.reportAccess === 'personal') {
      options = [userConfig];
    }
    else if (userConfig?.accessConfig.reportAccess === 'academy') {
      options = Object.values(allUsersWithSameTenant).filter((user) => user.accessConfig.reportAccess === 'academy');
    }
    else {
      options = Object.values(allUsersWithSameTenant).map((user) => (user));
    }

    if (!isFokusUser(userConfig)) {
      options = options.filter((user) => !isFokusUser(user));
    }

    options.sort((a, b) => getSortDistanceAccess(a, b));
    return options;
  }, [allUsersWithSameTenant, userConfig]);


  const tenantColor = getTenantColor(userConfig?.club ?? '');


  return (
    <div className='archive-report-filters-container'>

      <div className='archive-report-filters-dynamic-filters-container'>
        <div className='archive-report-filters-drop-down archive-report-filters-name'>
          <InputField
            value={reportFilters.nameSearchString}
            placeholder={translate(width < 1300 ? 'name' : 'searchForName', userConfig?.language)}
            onChange={(event) => setReportFilters({ ...reportFilters, nameSearchString: event.target.value })}
            resetValue={() => setReportFilters({ ...reportFilters, nameSearchString: '' })}
          />
        </div>

        <div className='archive-report-filters-drop-down-title archive-report-filters-report-type'>
          {translate('reportType', userConfig?.language)}
        </div>
        <div className='archive-report-filters-drop-down archive-report-filters-report-type'>
          <DropDownWithIcons
            id={'archive-report-filters-report-type'}
            dropDownOptions={['ScoutingReport', 'MatchReport', 'Comment', 'Attachment']}
            selectedOptions={reportFilters.selectedReportTypes}
            setSelectedOptions={(value: string[]) => setReportFilters({ ...reportFilters, selectedReportTypes: value as ReportType[] })}
            isDropDownExpanded={currentReportFilterExpanded === 'reportTypes'}
            setIsDropDownExpanded={(value: boolean) => setCurrentReportFilterExpanded(value ? 'reportTypes' : undefined)}
            defaultDropDownText={translate('select', userConfig?.language)}
            defaultDropDownTextColor={'#00000080'}
            marginBetweenOptions={2}
            maxHeight={'84vh'}
            multiSelect={true}
            useReportIcons={true}
          />
        </div>

        <div className='archive-report-filters-drop-down-title archive-report-filters-users'>
          {translate('user', userConfig?.language)}
        </div>
        <div className='archive-report-filters-drop-down archive-report-filters-users'>
          <UserDropDown
            id={'archive-report-filters-users'}
            userMap={allUsersWithSameTenant}
            dropDownOptions={userDropDownOptions}
            selectedOptions={userConfig?.accessConfig.reportAccess === 'personal' ? [userConfig.email] : reportFilters.selectedUsers}
            setSelectedOptions={(value: string[]) => setReportFilters({ ...reportFilters, selectedUsers: value })}
            isDropDownExpanded={currentReportFilterExpanded === 'users'}
            setIsDropDownExpanded={(value: boolean) => setCurrentReportFilterExpanded(value ? 'users' : undefined)}
            defaultDropDownText={translate('select', userConfig?.language)}
            defaultDropDownTextColor={'#00000080'}
            marginBetweenOptions={2}
            maxHeight={'84vh'}
            multiSelect={userConfig?.accessConfig.reportAccess !== 'personal'}
            dropDownDisabled={userConfig?.accessConfig.reportAccess === 'personal'}
          />
        </div>
      </div>

      <div className='archive-report-filters-drop-down-title archive-report-filters-ratings'>
        {translate('minimumOverallRating', userConfig?.language)}
      </div>
      <div className='archive-report-filters-drop-down archive-report-filters-ratings flex-column align-center'>
        <Rating
          name='overallScore'
          value={reportFilters.selectedMinimumOverallRating.length > 0 ? reportFilters.selectedMinimumOverallRating[0] : 0}
          onChange={(_: React.SyntheticEvent, value: number | null) => setReportFilters({ ...reportFilters, selectedMinimumOverallRating: value ? [value] : [] })}
          max={5}
          precision={0.5}
          sx={{
            fontSize: 28,
            color: tenantColor,
            '&:hover': {
              color: tenantColor,
            },
            '& .MuiRating-iconEmpty': {
              color: '#a7a9ad'
            },
          }}
        />
      </div>

      <IconButton
        onClick={() => setReportFilters(initialReportFiltersState)}
        icon={<ReplayIcon style={{ fontSize: 20 }} />}
        size={24}
        style={{ position: 'absolute', marginTop: 41, right: '2.5%' }}
        startWhite={true}
        title={translate('resetFilters', userConfig?.language)}
      />

    </div>
  );
};
